import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck, Wallet as WalletIcon, Handshake, Scale } from "lucide-react";

const Landing = () => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-background/80 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center text-primary-foreground font-bold">P</div>
            <span className="font-display text-xl font-bold tracking-tight">PAYD</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#for-who" className="hover:text-foreground transition-colors">Who it's for</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard"><Button data-testid="nav-app">Go to dashboard <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" data-testid="nav-login">Sign in</Button></Link>
                <Link to="/register"><Button data-testid="nav-signup">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-24 pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              Stop chasing<br />
              <span className="text-primary">invoices.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Set the terms upfront, deliver the work, and get paid when it's complete.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {user ? (
                <Link to="/dashboard">
                  <Button size="lg" className="h-12 px-7" data-testid="hero-cta-app">
                    Continue to app <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="h-12 px-7" data-testid="hero-cta-signup">
                      Start a contract <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button size="lg" variant="outline" className="h-12 px-7" data-testid="hero-cta-login">
                      I have an account
                    </Button>
                  </Link>
                </>
              )}
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              <span>Dispute-protected</span>
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up stagger-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 to-transparent blur-2xl rounded-3xl" />
              <div className="relative bg-card border border-border rounded-2xl p-6 shadow-xl">
                <div className="flex items-center justify-between pb-4 border-b border-border/60">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Contract</div>
                    <div className="font-display font-semibold">Brand redesign — Acme</div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">Active</span>
                </div>
                <div className="mt-4 space-y-3">
                  {[
                    { t: "Discovery", a: 1200, s: "Released" },
                    { t: "Wireframes", a: 1800, s: "Submitted" },
                    { t: "Final design", a: 2400, s: "Funded" },
                  ].map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/60">
                      <div>
                        <div className="font-medium text-sm">{m.t}</div>
                        <div className="text-xs text-muted-foreground">${m.a.toLocaleString()}</div>
                      </div>
                      <span className="text-xs font-semibold text-primary">{m.s}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between text-sm pt-4 border-t border-border/60">
                  <span className="text-muted-foreground">Reserved Pay Out</span>
                  <span className="font-display font-bold text-lg">$4,200</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="border-t border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">How it works</div>
            <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">Four steps. Zero drama.</h2>
          </div>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Handshake, t: "1. Agree", d: "Business or freelancer creates a contract, defines milestones, invites the other party." },
              { icon: WalletIcon, t: "2. Fund", d: "Each milestone is funded into escrow before work begins. Nothing to chase." },
              { icon: ShieldCheck, t: "3. Deliver", d: "Freelancer submits work. Business approves. Payment is released instantly." },
              { icon: Scale, t: "4. Protected", d: "If things go sideways, open a dispute. Admin resolves. Escrow is safe." },
            ].map((s, i) => (
              <div key={i} className={`bg-card border border-border rounded-xl p-6 animate-fade-up stagger-${i + 1}`}>
                <s.icon className="w-6 h-6 text-primary" />
                <div className="mt-6 font-display font-semibold text-lg">{s.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="for-who" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-10">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">For businesses</div>
            <h3 className="mt-3 text-3xl font-bold tracking-tight">Pay only for work you approve.</h3>
            <ul className="mt-6 space-y-3 text-muted-foreground">
              <li>• Pay freelancers with clear scope + due dates</li>
              <li>• Fewer invoice emails and follow-ups</li>
              <li>• Simplified tracking for accounting and reporting</li>
              <li>• Built-in dispute protections</li>
            </ul>
          </div>
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 sm:p-10">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">For freelancers</div>
            <h3 className="mt-3 text-3xl font-bold tracking-tight">Know the money is there before you start.</h3>
            <ul className="mt-6 space-y-3 text-muted-foreground">
              <li>• Get paid when the work is approved</li>
              <li>• No chasing invoices — approvals + payouts are tracked in one place</li>
              <li>• Clear milestones so scope + payment are always aligned</li>
              <li>• Disputes pause payout until it's resolved</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary grid place-items-center text-primary-foreground text-xs font-bold">P</div>
            <span>© {new Date().getFullYear()} PAYD</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
